/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect, useRef } from 'react';

import type { ProductType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import AISearchMarkdown from 'bundles/search-common/components/AISearchMarkdown';
import AISummary from 'bundles/search-common/components/AISummary';
import { LEARNING_PRODUCTS_ENUM_MAP } from 'bundles/search-common/constants';
import useAISummary from 'bundles/search-common/hooks/useAISummary';
import type { SearchProductHit, SearchRequestOrigin, SearchSource } from 'bundles/search-common/providers/searchTypes';
import { eventingV3ProductType, extractEventId } from 'bundles/search-common/utils/eventingUtils';

type Props = {
  searchQuery: string;
  searchResults: SearchProductHit[];
  onQueryClick?: (query: string) => void;
  userContext?: {
    userId?: string;
    preferences?: string[];
  };

  requestOrigin: SearchRequestOrigin;
  source: SearchSource;
};

/**
 * Container component that handles AI summary logic and renders the AISummary component
 *
 * @example
 * ```tsx
 * <AISummaryContainer
 *   searchQuery="machine learning"
 *   searchResults={searchResults}
 *   onQueryClick={(query) => performNewSearch(query)}
 *   requestOrigin={{ pageType: REQUEST_ORIGIN_PAGE_TYPE.SERP, segmentType: REQUEST_ORIGIN_SEGMENT_TYPE.CONSUMER }}
 * />
 * ```
 */
const AISummaryContainer = ({
  searchQuery,
  searchResults,
  onQueryClick,
  userContext,
  requestOrigin,
  source,
}: Props) => {
  const track = useTracker();
  const summaryRef = useRef<HTMLDivElement>(null);

  const { summarizeResults, loading, data, error } = useAISummary();

  useEffect(() => {
    if (searchQuery.trim() && searchResults.length > 0 && !loading && !data) {
      summarizeResults(searchQuery, searchResults, requestOrigin, userContext);
    }
  }, [searchQuery, searchResults, loading, data, summarizeResults, userContext, requestOrigin]);

  useEffect(() => {
    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');

      if (link && link.href) {
        track('click_page_section', {
          pageSection: {
            sectionName: 'search_ai_summary',
          },
        });

        const matchedResult = searchResults.find((result) => {
          if (result.__typename === 'Search_ProductHit' && result.url) {
            const linkPath = new URL(link.href).pathname;
            return linkPath.includes(result.url) || result.url.includes(linkPath);
          }
          return false;
        }) as SearchProductHit | undefined;

        if (matchedResult) {
          const productId = matchedResult.id || '';
          const productType = LEARNING_PRODUCTS_ENUM_MAP[matchedResult.productType || ''];
          const productSlug = matchedResult.url || link.href;

          track('click_product_card', {
            product: {
              id: extractEventId(productId),
              name: matchedResult.name || '',
              slug: productSlug,
              type: eventingV3ProductType(productType) as ProductType,
            },
            productCard: {
              index: 0,
              algoliaIndexName: source.indexName ?? '',
            },
            searchedQuery: searchQuery,
          });
        }
        // No event sent if no matching search result found
      }
    };

    const summaryElement = summaryRef.current;
    if (summaryElement) {
      summaryElement.addEventListener('click', handleLinkClick);
    }

    return () => {
      if (summaryElement) {
        summaryElement.removeEventListener('click', handleLinkClick);
      }
    };
  }, [track, searchResults, searchQuery, source]);

  // Don't render if there's no search query or results
  if (!searchQuery.trim() || searchResults.length === 0) {
    return null;
  }

  // Don't render if there was an error and no data
  if (error && !data) {
    // AI Summary failed to load - fail silently
    return null;
  }

  // Don't render if there's no summary data
  if (data && !data.summary) {
    return null;
  }

  return (
    <div ref={summaryRef}>
      <AISummary
        summary={<AISearchMarkdown source={data?.summary} />}
        suggestedQueries={data?.suggestions}
        isLoading={loading}
        onQueryClick={onQueryClick}
      />
    </div>
  );
};

export default AISummaryContainer;
